/**
 * SHER-1049. What an outpatient appointment counts as, as the API classifies it
 * (sherwood-api `utils/outpatientAppointmentType.ts`): one of the four standard types, a site's
 * own service under the name the site gave it, or Other. The API also sends the types present in
 * a response in display order, so this module owns presentation only — no list of types.
 */
export type AppointmentTypeKind = 'standard' | 'site' | 'other';

export interface AppointmentTypeRef {
  /** `kind:label` — what rows and per-type counts are keyed on. */
  key: string;
  kind: AppointmentTypeKind;
  label: string;
}

// Keyed on the API's own standard label, which must not be translated. `id` is the short form
// that keeps the by-type card narrow enough to sit beside its chart; `longId` is the professional
// table's header, which names the code. `color` travels with the series because the displayed
// name is translated at runtime and would otherwise miss categoryColorMap and land on the hash.
const STANDARD_TYPES: Record<string, { id: string; longId: string; color: string }> = {
  'First Visit': {
    id: 'hospital.analytics.appointment_types.first_visit',
    longId: 'hospital.analytics.clinical_activity.outp001',
    color: '#1E88E5',
  },
  'Follow-up < 15 days': {
    id: 'hospital.analytics.appointment_types.follow_up_lt_15',
    longId: 'hospital.analytics.clinical_activity.outp002',
    color: '#43A047',
  },
  'Follow-up > 15 days': {
    id: 'hospital.analytics.appointment_types.follow_up_gt_15',
    longId: 'hospital.analytics.clinical_activity.outp003',
    color: '#8E24AA',
  },
  Session: {
    id: 'hospital.analytics.appointment_types.session',
    longId: 'hospital.analytics.clinical_activity.outp004',
    color: '#FFB300',
  },
};

const OTHER_TRANSLATION_ID = 'hospital.analytics.appointment_types.other';
const OTHER_COLOR = '#9E9E9E';

/**
 * Standard types and Other are translated; a site's own service is shown exactly as the site
 * named it — that name is configuration, not copy.
 */
export function appointmentTypeLabel(
  type: AppointmentTypeRef,
  translate: (id: string) => unknown,
  variant: 'short' | 'long',
): string {
  if (type.kind === 'other') {
    return translate(OTHER_TRANSLATION_ID) as string;
  }
  const standard = type.kind === 'standard' ? STANDARD_TYPES[type.label] : undefined;
  if (!standard) {
    return type.label;
  }
  return translate(variant === 'short' ? standard.id : standard.longId) as string;
}

/** Undefined for a site service: the chart derives one from the series name. */
export function appointmentTypeColor(type: AppointmentTypeRef): string | undefined {
  if (type.kind === 'other') {
    return OTHER_COLOR;
  }
  return type.kind === 'standard' ? STANDARD_TYPES[type.label]?.color : undefined;
}

export function normalizeDepartmentTypePercentages(values: number[]): number[] {
  const total = values.reduce((sum, value) => sum + value, 0);
  if (total <= 0) {
    return values.map(() => 0);
  }

  const scaled = values.map((value) => (value / total) * 100);
  const rounded = scaled.map((value) => Number(value.toFixed(1)));
  const drift = Number((100 - rounded.reduce((sum, value) => sum + value, 0)).toFixed(1));
  if (drift !== 0 && rounded.length > 0) {
    const lastIndex = rounded.length - 1;
    rounded[lastIndex] = Number((rounded[lastIndex] + drift).toFixed(1));
  }
  return rounded;
}
